<template>
    <div class="p-6">
      <h2 class="text-xl font-semibold mb-4">Asset 히트맵</h2>
  
      <!-- 회사 및 이미지 선택 -->
      <div class="mb-4 flex gap-4">
        <select v-model="selectedImage" class="border p-2">
          <option disabled value="">배경 이미지 선택</option>
          <option v-for="bg in backgrounds" :key="bg.id" :value="bg">
            {{ bg.description || bg.background_image }}
          </option>
        </select>
        <select v-model="selectedPeriod" class="border p-2">
          <option value="1">최근 1일</option>
          <option value="7">최근 7일</option>
          <option value="30">최근 30일</option>
        </select>
        <button class="bg-blue-500 text-white px-4 py-2 rounded" @click="loadHeatmap">조회</button>
      </div>
  
      <!-- 히트맵 캔버스 -->
      <div v-if="selectedImage" class="relative border inline-block">
        <img :src="`/assets/${selectedImage.background_image}`" :style="imageStyle" />
        <canvas
          ref="heatmapCanvas"
          :width="selectedImage.width"
          :height="selectedImage.height"
          class="absolute top-0 left-0"
          @click="handleClick"
        ></canvas>
  
        <!-- 클릭된 포인트 정보 -->
        <div v-if="selectedPoint" class="absolute bg-white text-sm p-2 shadow border rounded" :style="pointStyle">
          <p><strong>GW:</strong> {{ selectedPoint.gw_mac }}</p>
          <p><strong>Count:</strong> {{ selectedPoint.count }}</p>
          <button class="text-xs text-gray-500 mt-1" @click="selectedPoint = null">닫기</button>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, watch, computed } from 'vue';
  import axios from 'axios';
  
  const company = 'SDAA';
  const backgrounds = ref([]);
  const selectedImage = ref(null);
  const selectedPeriod = ref(1);
  const heatmapCanvas = ref(null);
  const heatmapData = ref([]);
  const selectedPoint = ref(null);
  const autoRefreshInterval = ref(null);
  
  const imageStyle = {
    width: '100%',
    display: 'block'
  };
  
  const pointStyle = computed(() => {
    if (!selectedPoint.value) return {};
    return {
      left: selectedPoint.value.x + 'px',
      top: selectedPoint.value.y + 'px',
      transform: 'translate(-50%, -100%)',
      zIndex: 10
    };
  });
  
  async function loadBackgrounds() {
    const res = await axios.get(`/api/backgrounds?company=${company}`);
    backgrounds.value = res.data;
  }
  
  async function loadHeatmap() {
    if (!selectedImage.value) return;
  
    const res = await axios.get('/api/asset-heatmap', {
      params: {
        company,
        period: selectedPeriod.value,
        background_image: selectedImage.value.background_image
      }
    });
    heatmapData.value = res.data;
    drawHeatmap(res.data);
  }
  
  function drawHeatmap(data) {
    const canvas = heatmapCanvas.value;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  
    data.forEach(point => {
      const intensity = Math.min(1, point.count / 100);
      const radius = 25;
      const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, radius);
  
      if (intensity > 0.75) {
        gradient.addColorStop(0, `rgba(255, 0, 0, ${intensity})`);
      } else if (intensity > 0.5) {
        gradient.addColorStop(0, `rgba(255, 165, 0, ${intensity})`);
      } else if (intensity > 0.25) {
        gradient.addColorStop(0, `rgba(255, 255, 0, ${intensity})`);
      } else {
        gradient.addColorStop(0, `rgba(0, 255, 0, ${intensity})`);
      }
      gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
      ctx.fill();
    });
  }
  
  function handleClick(event) {
    const canvas = heatmapCanvas.value;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
  
    const found = heatmapData.value.find(p => {
      const dx = p.x - x;
      const dy = p.y - y;
      return Math.sqrt(dx * dx + dy * dy) < 25;
    });
  
    if (found) {
      selectedPoint.value = { ...found, x, y };
    } else {
      selectedPoint.value = null;
    }
  }
  
  onMounted(() => {
    loadBackgrounds();
  
    autoRefreshInterval.value = setInterval(() => {
      if (selectedImage.value) loadHeatmap();
    }, 30000); // 30초 간격 자동 새로고침
  });
  
  watch(selectedImage, () => {
    selectedPoint.value = null;
    loadHeatmap();
  });
  </script>
  
  <style scoped>
  canvas {
    pointer-events: auto;
  }
  </style>